import { useEffect, useRef } from "preact/hooks";
import {
  EMIT_LOAD_COMPLETE,
  CARD_TRANSACTION_SUCCESS,
  ACH_TRANSACTION_SUCCESS,
} from "../../helpers/event-definitions";

import ACHPaymentInitiatedSection from "./ACHPaymentInitiatedSection";
import PaymentSuccessfulSection from "./PaymentSuccessfulSection";
import { Configs } from "../../helpers/context";
import { formatIntegerCurrencyString } from "../../helpers/formatters";
import { useContext } from "preact/hooks";
import InvoiceAlreadyPaidSection from "./InvoiceAlreadyPaidSection";

const EndingPage = ({ transaction, isDesktopOrLaptop, c2Analytics, payLink }) => {
  const topRef = useRef(null);

  // Scroll to top for Pay Button flows after successful payment
  useEffect(() => {
    const isPayButton =
      payLink?.sourceApp === "paybutton" ||
      window?.PayButtonsJS?.sourceApp === "paybutton" ||
      window.location.href.includes("paybutton");
    if (isPayButton && transaction) {
      // Use scrollIntoView on a ref at the top of the component — works reliably inside iframes
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: "auto", block: "start" });
      }
    }
  }, [transaction]);

  // track custom event on first load
  useEffect(() => {
    if (c2Analytics?.trackCustomEvent) {
      c2Analytics.trackCustomEvent(EMIT_LOAD_COMPLETE);
    }
  }, []);

  const isACH = transaction?.fundingSource?.type === "CHEQUE";
  const isCard = transaction?.fundingSource?.card?.source === "DIRECT";
  const configsContext = useContext(Configs);
  // Right after paying an Invoice, the payLink.invoice object isn't necessarily up to date
  // To be more flexible, we can check the transaction status as well as the invoice status
  // - This logic will change once we support partial payments for an Invoice
  const successfulTransaction =
    transaction?.status === "AUTHORIZED" || transaction?.status === "CAPTURED";
  const isAlreadyPaidInvoice =
    payLink?.urlType === "N" && (payLink?.invoice?.status === "PAID" || successfulTransaction);
  const amount = formatIntegerCurrencyString(
    transaction?.processorResponse?.approvedAmount ||
      (isAlreadyPaidInvoice ? payLink?.amount : 0) ||
      0,
    configsContext?.configs?.currency,
    configsContext?.configs?.ecommerceLocaleLanguage,
  );
  const businessName = configsContext?.configs?.ecommerceBusinessName;
  const transactionId = payLink?.invoice?.paidByTransactionId || transaction?.id;

  const surchargeFee = (transaction?.amounts?.transactionFees || []).find((fee) => {
    return fee.feeProgramType === "SURCHARGE";
  });

  const surchargeAmount = surchargeFee?.amount || 0;

  const surchargeAmountString = formatIntegerCurrencyString(
    surchargeAmount,
    configsContext?.configs?.currency,
    configsContext?.configs?.ecommerceLocaleLanguage,
  );

  const unpaidPaymentRequests = (payLink?.invoice?.paymentRequests || []).filter(
    (pr) => pr.status === "NOT_PAID",
  );
  const isLastPaymentRequest = unpaidPaymentRequests.length === 1;
  const invoiceIsFullyPaid =
    (transaction.amounts?.transactionAmount || 0) >=
    (payLink?.remainingAmount || payLink?.invoice?.amount);
  const showViewRemainingAmountButton =
    payLink?.urlType === "N" && !invoiceIsFullyPaid && !isLastPaymentRequest;

  useEffect(() => {
    if (c2Analytics?.trackCustomEvent) {
      if (isACH) {
        c2Analytics.trackCustomEvent(ACH_TRANSACTION_SUCCESS);
      }

      if (isCard) {
        c2Analytics.trackCustomEvent(CARD_TRANSACTION_SUCCESS);
      }
    }
  }, [isACH, isCard]);

  return (
    <>
      <div ref={topRef} style={{ height: 0, overflow: "hidden" }} />
      {isAlreadyPaidInvoice ? (
        <InvoiceAlreadyPaidSection
          transactionId={transactionId}
          isDesktopOrLaptop={isDesktopOrLaptop}
          amount={amount}
          surchargeAmount={surchargeAmount ? surchargeAmountString : null}
          businessName={businessName}
          isPDFExperimentOn={payLink?.isPDFExperimentOn}
          c2Analytics={c2Analytics}
          showViewRemainingAmountButton={showViewRemainingAmountButton}
        />
      ) : isACH ? (
        <ACHPaymentInitiatedSection
          payLink={payLink}
          transactionId={transactionId}
          transaction={transaction}
          isDesktopOrLaptop={isDesktopOrLaptop}
          amount={amount}
          businessName={businessName}
          showViewRemainingAmountButton={showViewRemainingAmountButton}
        />
      ) : (
        <PaymentSuccessfulSection
          transaction={transaction}
          transactionId={transactionId}
          isDesktopOrLaptop={isDesktopOrLaptop}
          amount={amount}
          surchargeAmount={surchargeAmount ? surchargeAmountString : null}
          businessName={businessName}
          showViewRemainingAmountButton={showViewRemainingAmountButton}
          payLink={payLink}
        />
      )}
    </>
  );
};

export default EndingPage;
